import { cva, type VariantProps } from 'class-variance-authority'

export { default as Button } from './Button.vue'
export { default as ButtonIcon } from './ButtonIcon.vue'

export const buttonVariantList = {
  color: {
    primary: '',
    destructive: '',
    neutral: '',
    success: '',
  },
  variant: {
    solid: '',
    outline: 'border',
    soft: '',
    ghost: 'bg-transparent',
    link: 'bg-transparent underline underline-offset-4',
    'link-inline': 'bg-transparent hover:underline underline-offset-4 !h-auto !px-0',
  },
  size: {
    '2xs': 'h-6 gap-1 rounded-lg px-2 text-xs',
    xs: 'h-7 gap-[5px] rounded-lg px-[10px] text-xs',
    sm: 'h-8 gap-1.5 rounded-lg px-3 text-xs',
    md: 'h-9 gap-[7px] rounded-lg px-[14px] text-sm',
    lg: 'h-11 gap-2 rounded-lg px-4 text-base',
    xl: 'h-12 gap-2 rounded-lg px-4 text-base',
    '2xl': 'h-14 gap-2.5 rounded-lg px-5 text-lg/[26px]',
  },
  aspectSquare: {
    false: '',
    true: 'aspect-square px-0',
  },
}

export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md bg-clip-border font-medium outline-none ring-neutral-800 ring-offset-2 ring-offset-background transition-colors focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: buttonVariantList,
    compoundVariants: [
      {
        color: 'primary',
        variant: 'solid',
        class: 'bg-accent-600 text-white hover:bg-accent-700',
      },
      {
        color: 'primary',
        variant: 'outline',
        class: 'border-accent-600 text-accent-600 hover:bg-accent-50',
      },
      {
        color: 'primary',
        variant: 'soft',
        class: 'bg-accent-50 text-accent-600 hover:bg-accent-100',
      },
      {
        color: 'primary',
        variant: 'ghost',
        class: 'text-accent-600 hover:bg-accent-50',
      },
      {
        color: 'primary',
        variant: 'link',
        class: 'text-accent-600 hover:text-accent-700',
      },
      {
        color: 'primary',
        variant: 'link-inline',
        class: 'text-accent-600 hover:text-accent-700',
      },
      {
        color: 'destructive',
        variant: 'solid',
        class: 'bg-error-400 text-white hover:bg-error-500',
      },
      {
        color: 'destructive',
        variant: 'outline',
        class: 'border-error-600 text-error-600 hover:bg-error-50',
      },
      {
        color: 'destructive',
        variant: 'soft',
        class: 'bg-error-50 text-error-600 hover:bg-error-100',
      },
      {
        color: 'destructive',
        variant: 'ghost',
        class: 'text-error-600 hover:bg-error-50',
      },
      {
        color: 'destructive',
        variant: 'link',
        class: 'text-error-600 hover:text-error-700',
      },
      {
        color: 'destructive',
        variant: 'link-inline',
        class: 'text-error-600 hover:text-error-700',
      },
      {
        color: 'neutral',
        variant: 'solid',
        class: 'bg-neutral-600 text-white hover:bg-neutral-700',
      },
      {
        color: 'neutral',
        variant: 'outline',
        class: 'border-border text-neutral-800 hover:bg-neutral-50',
      },
      {
        color: 'neutral',
        variant: 'soft',
        class: 'bg-neutral-100 text-neutral-800 hover:bg-neutral-200',
      },
      {
        color: 'neutral',
        variant: 'ghost',
        class: 'text-neutral-800 hover:bg-neutral-alpha-100',
      },
      {
        color: 'neutral',
        variant: 'link',
        class: 'text-neutral-800 hover:text-neutral-900',
      },
      {
        color: 'neutral',
        variant: 'link-inline',
        class: 'text-neutral-800 hover:text-neutral-900',
      },
      {
        color: 'success',
        variant: 'solid',
        class: 'bg-success-600 text-white hover:bg-success-700',
      },
      {
        color: 'success',
        variant: 'outline',
        class: 'border-success-600 text-success-600 hover:bg-success-50',
      },
      {
        color: 'success',
        variant: 'soft',
        class: 'bg-success-50 text-success-600 hover:bg-success-100',
      },
      {
        color: 'success',
        variant: 'ghost',
        class: 'text-success-600 hover:bg-success-50',
      },
      {
        color: 'success',
        variant: 'link',
        class: 'text-success-600 hover:text-success-700',
      },
      {
        color: 'success',
        variant: 'link-inline',
        class: 'text-success-600 hover:text-success-700',
      },
    ],
  },
)

export const buttonIconVariantList: Record<string, Record<NonNullable<ButtonVariants['size']>, string>> = {
  size: {
    '2xs': 'size-[12px]',
    xs: 'size-[14px]',
    sm: 'size-[16px]',
    md: 'size-[20px]',
    lg: 'size-[20px]',
    xl: 'size-[20px]',
    '2xl': 'size-[24px]',
  },
}

export const buttonIconVariants = cva(
  '',
  {
    variants: buttonIconVariantList,
  },
)

export type ButtonVariants = VariantProps<typeof buttonVariants>
export type ButtonIconVariants = VariantProps<typeof buttonIconVariants>
